import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { TableState } from "../types";

/**
 * Initial state for table slice
 */
const initialState: TableState = {
  cellUpdates: {},
  editingCell: null,
  columnWidths: {},
  sortConfig: null,
  filters: {},
};

/**
 * Redux slice for table state management
 * Handles cell-level updates, editing state, column widths, sorting, and filtering
 */
const tableSlice = createSlice({
  name: "table",
  initialState,
  reducers: {
    /**
     * Update a specific cell value
     * @param state - Current table state
     * @param action - Payload containing rowId, columnId, and value
     */
    updateCell: (
      state,
      action: PayloadAction<{
        rowId: string;
        columnId: string;
        value: any;
      }>,
    ) => {
      const { rowId, columnId, value } = action.payload;
      if (!state.cellUpdates[rowId]) {
        state.cellUpdates[rowId] = {};
      }
      state.cellUpdates[rowId][columnId] = value;
    },

    /**
     * Batch update multiple cells
     * @param state - Current table state
     * @param action - Payload containing updates map
     */
    batchUpdateCells: (
      state,
      action: PayloadAction<Record<string, Record<string, any>>>,
    ) => {
      state.cellUpdates = {
        ...state.cellUpdates,
        ...action.payload,
      };
    },

    /**
     * Clear all cell updates for a specific row
     * @param state - Current table state
     * @param action - Payload containing rowId
     */
    clearRowUpdates: (state, action: PayloadAction<string>) => {
      const rowId = action.payload;
      delete state.cellUpdates[rowId];
    },

    /**
     * Clear all cell updates
     * @param state - Current table state
     */
    clearAllUpdates: (state) => {
      state.cellUpdates = {};
    },

    /**
     * Set the currently editing cell
     * @param state - Current table state
     * @param action - Payload containing rowId and columnId, or null to clear
     */
    setEditingCell: (
      state,
      action: PayloadAction<{ rowId: string; columnId: string } | null>,
    ) => {
      state.editingCell = action.payload;
    },

    /**
     * Update column width
     * @param state - Current table state
     * @param action - Payload containing columnId and width
     */
    setColumnWidth: (
      state,
      action: PayloadAction<{ columnId: string; width: number }>,
    ) => {
      const { columnId, width } = action.payload;
      state.columnWidths[columnId] = width;
    },

    /**
     * Batch update column widths
     * @param state - Current table state
     * @param action - Payload containing widths map
     */
    setColumnWidths: (state, action: PayloadAction<Record<string, number>>) => {
      state.columnWidths = {
        ...state.columnWidths,
        ...action.payload,
      };
    },

    /**
     * Set sort configuration
     * @param state - Current table state
     * @param action - Payload containing columnId and direction, or null to clear
     */
    setSortConfig: (
      state,
      action: PayloadAction<{
        columnId: string;
        direction: "asc" | "desc";
      } | null>,
    ) => {
      state.sortConfig = action.payload;
    },

    /**
     * Set filter for a column
     * @param state - Current table state
     * @param action - Payload containing columnId and filter value
     */
    setFilter: (
      state,
      action: PayloadAction<{ columnId: string; value: any }>,
    ) => {
      const { columnId, value } = action.payload;
      if (value === null || value === undefined || value === "") {
        delete state.filters[columnId];
      } else {
        state.filters[columnId] = value;
      }
    },

    /**
     * Clear all filters
     * @param state - Current table state
     */
    clearFilters: (state) => {
      state.filters = {};
    },

    /**
     * Reset table state to initial
     * @param state - Current table state
     */
    resetTableState: (_state) => {
      return initialState;
    },
  },
});

export const {
  updateCell,
  batchUpdateCells,
  clearRowUpdates,
  clearAllUpdates,
  setEditingCell,
  setColumnWidth,
  setColumnWidths,
  setSortConfig,
  setFilter,
  clearFilters,
  resetTableState,
} = tableSlice.actions;

export default tableSlice.reducer;
